import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  Select,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  FormControl,
  FormLabel,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  useColorModeValue,
  IconButton,
  Checkbox,
  CheckboxGroup,
  Stack,
} from "@chakra-ui/react";
import { MdFilterList, MdClose } from "react-icons/md";

/**
 * FilterSystem Component
 *
 * A robust, reusable filter system with field-type dependent controls
 *
 * Props:
 * - filterFields: Array of filter field definitions
 *   Each field should have:
 *   {
 *     key: string,           // Unique identifier for the field
 *     label: string,         // Display label
 *     type: 'text' | 'select' | 'multiselect' | 'date' | 'number' | 'daterange',
 *     options?: Array,       // For select/multiselect type: [{value, label}] or [string]
 *     placeholder?: string,  // Placeholder text
 *   }
 * - onFilterChange: (filters) => void  // Callback when filters change
 * - initialFilters?: Object            // Initial filter values
 */

const FilterSystem = ({ filterFields, onFilterChange, initialFilters = {}, showTags = true, externalFilters }) => {
  const [filters, setFilters] = useState(initialFilters);
  const [tempFilters, setTempFilters] = useState(initialFilters);
  const [isOpen, setIsOpen] = useState(false);

  // Sync internal state with external filters prop
  React.useEffect(() => {
    if (externalFilters) {
      setFilters(externalFilters);
      setTempFilters(externalFilters);
    }
  }, [externalFilters]);

  const bgColor = useColorModeValue("white", "#1B254B");
  const tagBg = useColorModeValue("blue.50", "blue.900");
  const tagColor = useColorModeValue("blue.700", "blue.200");

  const handleTempFilterChange = (key, value) => {
    setTempFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    onFilterChange(tempFilters);
    setIsOpen(false);
  };

  const clearAllFilters = () => {
    const emptyFilters = {};
    filterFields.forEach((field) => {
      if (field.type === "daterange") {
        emptyFilters[`${field.key}_from`] = "";
        emptyFilters[`${field.key}_to`] = "";
      } else if (field.type === "multiselect") {
        emptyFilters[field.key] = [];
      } else {
        emptyFilters[field.key] = "";
      }
    });
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const removeFilter = (key) => {
    const newFilters = { ...filters };

    // Handle daterange fields
    if (key.includes("_from") || key.includes("_to")) {
      const baseKey = key.replace(/_from|_to/, "");
      delete newFilters[`${baseKey}_from`];
      delete newFilters[`${baseKey}_to`];
      const newTempFilters = { ...tempFilters };
      delete newTempFilters[`${baseKey}_from`];
      delete newTempFilters[`${baseKey}_to`];
      setTempFilters(newTempFilters);
    } else {
      delete newFilters[key];
      const newTempFilters = { ...tempFilters };
      delete newTempFilters[key];
      setTempFilters(newTempFilters);
    }

    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter((value) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== "" && value !== undefined && value !== null;
    }).length;
  };

  const getFilterDisplayValue = (field, value) => {
    if (field.type === "multiselect" && Array.isArray(value) && field.options) {
      return value.map((v) => {
        const option = field.options.find((opt) => {
          const optValue = typeof opt === "string" ? opt : opt.value;
          return optValue === v;
        });
        return option ? (typeof option === "string" ? option : option.label) : v;
      }).join(", ");
    }
    if (field.type === "select" && field.options) {
      const option = field.options.find((opt) => {
        const optValue = typeof opt === "string" ? opt : opt.value;
        return optValue === value;
      });
      if (option) {
        return typeof option === "string" ? option : option.label;
      }
    }
    return value;
  };

  const renderFilterControl = (field) => {
    const value = tempFilters[field.key] || "";

    switch (field.type) {
      case "text":
      case "number":
        return (
          <Input
            type={field.type}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            value={value}
            onChange={(e) => handleTempFilterChange(field.key, e.target.value)}
            bg={bgColor}
          />
        );

      case "select":
        return (
          <Select
            placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`}
            value={value}
            onChange={(e) => handleTempFilterChange(field.key, e.target.value)}
            bg={bgColor}
          >
            {field.options?.map((option) => {
              const optValue = typeof option === "string" ? option : option.value;
              const optLabel = typeof option === "string" ? option : option.label;
              return (
                <option key={optValue} value={optValue}>
                  {optLabel}
                </option>
              );
            })}
          </Select>
        );

      case "multiselect":
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <Box
            maxH="200px"
            overflowY="auto"
            border="1px solid"
            borderColor={useColorModeValue("gray.200", "whiteAlpha.300")}
            borderRadius="md"
            p={2}
            bg={bgColor}
          >
            <CheckboxGroup
              value={selectedValues}
              onChange={(newValues) => handleTempFilterChange(field.key, newValues)}
            >
              <Stack spacing={2}>
                {field.options?.map((option) => {
                  const optValue = typeof option === "string" ? option : option.value;
                  const optLabel = typeof option === "string" ? option : option.label;
                  return (
                    <Checkbox key={optValue} value={optValue}>
                      {optLabel}
                    </Checkbox>
                  );
                })}
              </Stack>
            </CheckboxGroup>
          </Box>
        );

      case "date":
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleTempFilterChange(field.key, e.target.value)}
            bg={bgColor}
          />
        );

      case "daterange":
        return (
          <Flex gap={2} direction={{ base: "column", sm: "row" }}>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>
                From
              </FormLabel>
              <Input
                type="date"
                value={tempFilters[`${field.key}_from`] || ""}
                onChange={(e) => handleTempFilterChange(`${field.key}_from`, e.target.value)}
                bg={bgColor}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>
                To
              </FormLabel>
              <Input
                type="date"
                value={tempFilters[`${field.key}_to`] || ""}
                onChange={(e) => handleTempFilterChange(`${field.key}_to`, e.target.value)}
                bg={bgColor}
              />
            </FormControl>
          </Flex>
        );

      default:
        return null;
    }
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <>
      <Flex gap={3} alignItems="center" justifyContent="flex-end">
        <Popover
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          placement="bottom-end"
          closeOnBlur={true}
        >
          <PopoverTrigger>
            <Button
              leftIcon={<Icon as={MdFilterList} />}
              colorScheme="blue"
              onClick={() => setIsOpen(!isOpen)}
              position="relative"
            >
              Filters
              {activeFilterCount > 0 && (
                <Box
                  position="absolute"
                  top="-8px"
                  right="-8px"
                  bg="red.500"
                  color="white"
                  borderRadius="full"
                  fontSize="xs"
                  fontWeight="bold"
                  w="20px"
                  h="20px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {activeFilterCount}
                </Box>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent width={{ base: "90vw", sm: "500px" }} maxH="70vh" display="flex" flexDirection="column">
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader fontWeight="bold" flexShrink={0}>Filter Options</PopoverHeader>
            <PopoverBody
              flex="1"
              overflowY="auto"
              overflowX="hidden"
              css={{
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
            >
              <Flex direction="column" gap={4} pb={2}>
                {filterFields.map((field) => (
                  <FormControl key={field.key}>
                    <FormLabel fontSize="sm" fontWeight="semibold">
                      {field.label}
                    </FormLabel>
                    {renderFilterControl(field)}
                  </FormControl>
                ))}
              </Flex>
            </PopoverBody>
            <Box
              borderTop="1px solid"
              borderColor={useColorModeValue("gray.200", "whiteAlpha.300")}
              p={3}
              flexShrink={0}
              bg={useColorModeValue("gray.50", "gray.800")}
            >
              <Flex gap={2}>
                <Button colorScheme="blue" flex={1} onClick={applyFilters}>
                  Apply Filters
                </Button>
                <Button
                  variant="outline"
                  colorScheme="red"
                  flex={1}
                  onClick={() => {
                    const emptyFilters = {};
                    filterFields.forEach((field) => {
                      if (field.type === "daterange") {
                        emptyFilters[`${field.key}_from`] = "";
                        emptyFilters[`${field.key}_to`] = "";
                      } else if (field.type === "multiselect") {
                        emptyFilters[field.key] = [];
                      } else {
                        emptyFilters[field.key] = "";
                      }
                    });
                    setTempFilters(emptyFilters);
                  }}
                >
                  Clear
                </Button>
              </Flex>
            </Box>
          </PopoverContent>
        </Popover>

        {activeFilterCount > 0 && (
          <Button
            size="sm"
            variant="ghost"
            colorScheme="red"
            leftIcon={<Icon as={MdClose} />}
            onClick={clearAllFilters}
          >
            Clear All
          </Button>
        )}
      </Flex>

      {/* Active Filters Display */}
      {showTags && activeFilterCount > 0 && (
        <Wrap spacing={2} mt={3}>
          {filterFields.map((field) => {
            if (field.type === "daterange") {
              const fromValue = filters[`${field.key}_from`];
              const toValue = filters[`${field.key}_to`];
              if (fromValue || toValue) {
                return (
                  <WrapItem key={field.key}>
                    <Tag
                      size="md"
                      borderRadius="full"
                      variant="solid"
                      bg={tagBg}
                      color={tagColor}
                    >
                      <TagLabel>
                        {field.label}: {fromValue || "..."} to {toValue || "..."}
                      </TagLabel>
                      <TagCloseButton onClick={() => removeFilter(`${field.key}_from`)} />
                    </Tag>
                  </WrapItem>
                );
              }
            } else {
              const value = filters[field.key];
              const hasValue = field.type === "multiselect"
                ? Array.isArray(value) && value.length > 0
                : value;

              if (hasValue) {
                return (
                  <WrapItem key={field.key}>
                    <Tag
                      size="md"
                      borderRadius="full"
                      variant="solid"
                      bg={tagBg}
                      color={tagColor}
                    >
                      <TagLabel>
                        {field.label}: {getFilterDisplayValue(field, value)}
                      </TagLabel>
                      <TagCloseButton onClick={() => removeFilter(field.key)} />
                    </Tag>
                  </WrapItem>
                );
              }
            }
            return null;
          })}
        </Wrap>
      )}
    </>
  );
};

// Export helper functions for use in parent components
export const getActiveFilterCount = (filters) => {
  return Object.values(filters).filter((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== "" && value !== undefined && value !== null;
  }).length;
};

export const getFilterDisplayValue = (field, value, filters) => {
  if (field.type === "multiselect" && Array.isArray(value) && field.options) {
    return value.map((v) => {
      const option = field.options.find((opt) => {
        const optValue = typeof opt === "string" ? opt : opt.value;
        return optValue === v;
      });
      return option ? (typeof option === "string" ? option : option.label) : v;
    }).join(", ");
  }
  if (field.type === "select" && field.options) {
    const option = field.options.find((opt) => {
      const optValue = typeof opt === "string" ? opt : opt.value;
      return optValue === value;
    });
    if (option) {
      return typeof option === "string" ? option : option.label;
    }
  }
  return value;
};

export const removeFilter = (key, filters, setFilters, onFilterChange, filterFields) => {
  const newFilters = { ...filters };

  // Handle daterange fields
  if (key.includes("_from") || key.includes("_to")) {
    const baseKey = key.replace(/_from|_to/, "");
    delete newFilters[`${baseKey}_from`];
    delete newFilters[`${baseKey}_to`];
  } else {
    delete newFilters[key];
  }

  setFilters(newFilters);
  onFilterChange(newFilters);
};

export const clearAllFilters = (filterFields, setFilters, onFilterChange) => {
  const emptyFilters = {};
  filterFields.forEach((field) => {
    if (field.type === "daterange") {
      emptyFilters[`${field.key}_from`] = "";
      emptyFilters[`${field.key}_to`] = "";
    } else if (field.type === "multiselect") {
      emptyFilters[field.key] = [];
    } else {
      emptyFilters[field.key] = "";
    }
  });
  setFilters(emptyFilters);
  onFilterChange(emptyFilters);
};

export default FilterSystem;
